import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useCallback, useState } from "react";
import {
  useDismissWorklistClusterMutation,
  useUndismissWorklistClusterMutation,
} from "store/queries/benefitComparison";
import {
  IWorklistClusterKey,
  IWorklistRow,
} from "types/BenefitComparison/BenefitComparison";

import { COMPARISON_BENEFIT_LABELS } from "./benefitComparisonWording";
import { ICelebrationOrigin, WorklistCelebration } from "./WorklistCelebration";

/**
 * "HIDE THIS CLUSTER" — TAKE A WHOLE WORKLIST CLUSTER OFF THE LIST.
 *
 * Neutral on purpose: a reviewer hides a cluster when the engine is right, but also when the cause
 * is elsewhere (the order's payer was never switched, a data or configuration problem upstream).
 * The stored reason is still `ENGINE_CORRECT` — the only enum value — and is not shown.
 *
 * 🚨 IT WRITES NO VERDICT. Marking every check in the cluster with a verdict was rejected: verdicts are
 * scored truth, so it would count values nobody reviewed as verified and inflate the engine's
 * accuracy. This only takes the row off the list — and puts it back, tagged, the moment today's
 * engine reads a value outside the range it had when it was marked.
 */

export const DISMISSED_TAG = "Hidden";
export const STALE_TAG = "Engine changed since hidden";

export const clusterKeyOf = (row: IWorklistRow): IWorklistClusterKey | null =>
  row.benefit === null
    ? null
    : {
        accountId: row.accountId,
        payerId: row.payerId,
        planName: row.planName,
        benefit: row.benefit,
        readFrom: row.readFrom,
      };

const dateOf = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/**
 * The row's dismissal cell: the tag (with the note as its tooltip) and Undo when the cluster is
 * marked, and the button that marks it when it is not — or when the mark has gone stale.
 */
export function WorklistDismissalActions({
  row,
  tooltipClass,
  onDismiss,
}: {
  row: IWorklistRow;
  tooltipClass: string;
  onDismiss: (row: IWorklistRow) => void;
}) {
  const [undismiss, { isLoading }] = useUndismissWorklistClusterMutation();
  const key = clusterKeyOf(row);
  // A configuration gap covers every benefit and is not a cluster the engine answered.
  if (key === null) return null;
  const { dismissal } = row;
  return (
    <div className="flex flex-column align-items-start gap-1">
      {dismissal && (
        <Tag
          value={dismissal.stale ? STALE_TAG : DISMISSED_TAG}
          severity={dismissal.stale ? "warning" : "success"}
          className={`white-space-nowrap ${tooltipClass}`}
          data-pr-tooltip={`${dateOf(dismissal.dismissedAt)}${
            dismissal.note ? ` — ${dismissal.note}` : ""
          }`}
          data-testid={
            dismissal.stale
              ? "benefitWorklist.dismissal.stale"
              : "benefitWorklist.dismissal"
          }
        />
      )}
      {dismissal && (
        <span className="text-xs text-color-secondary white-space-nowrap">
          {dateOf(dismissal.dismissedAt)}
        </span>
      )}
      <div className="flex gap-1">
        {(!dismissal || dismissal.stale) && (
          <Button
            type="button"
            label="Hide cluster"
            className="p-button-text p-button-sm white-space-nowrap"
            data-testid="benefitWorklist.dismiss"
            onClick={() => onDismiss(row)}
          />
        )}
        {dismissal && (
          <Button
            type="button"
            label="Undo"
            className="p-button-text p-button-sm"
            loading={isLoading}
            data-testid="benefitWorklist.undismiss"
            onClick={() => undismiss(key)}
          />
        )}
      </div>
    </div>
  );
}

/** The small confirm behind "Hide cluster", with an optional note saying why. */
export function DismissClusterDialog({
  row,
  onHide,
}: {
  row: IWorklistRow | null;
  onHide: () => void;
}) {
  const [note, setNote] = useState("");
  // CLE-2895 — where the confirm button was when a dismiss SUCCEEDED; null = no celebration showing.
  const [celebration, setCelebration] = useState<ICelebrationOrigin | null>(
    null
  );
  const endCelebration = useCallback(() => setCelebration(null), []);
  const [dismiss, { isLoading, isError }] = useDismissWorklistClusterMutation();
  const key = row ? clusterKeyOf(row) : null;
  const close = () => {
    setNote("");
    onHide();
  };
  return (
    <>
      {celebration && (
        <WorklistCelebration origin={celebration} onDone={endCelebration} />
      )}
      <Dialog
        header="Hide this cluster?"
        visible={key !== null}
        style={{ width: "min(32rem, 95vw)" }}
        onHide={close}
        footer={
          <>
            <Button
              type="button"
              label="Cancel"
              className="p-button-text"
              onClick={close}
            />
            <Button
              type="button"
              label="Hide this cluster"
              loading={isLoading}
              data-testid="benefitWorklist.dismiss.confirm"
              onClick={async (event) => {
                if (!key) return;
                // Read before the await: the button is gone once the dialog closes.
                const box = event.currentTarget.getBoundingClientRect();
                const result = await dismiss({
                  ...key,
                  ...(note.trim() ? { note: note.trim() } : {}),
                });
                if (!("error" in result)) {
                  setCelebration({
                    x: box.left + box.width / 2,
                    y: box.top + box.height / 2,
                  });
                  close();
                }
              }}
            />
          </>
        }
        data-testid="benefitWorklist.dismiss.dialog"
      >
        {row && key && (
          <div className="flex flex-column gap-3">
            <span>
              {row.accountName} · {row.payerName} ·{" "}
              {row.planName ?? "No plan name"} ·{" "}
              {COMPARISON_BENEFIT_LABELS[key.benefit]}
            </span>
            <span className="text-color-secondary">
              Hiding takes this cluster off the worklist, whether the engine is
              right here or the cause is elsewhere (for example the order&apos;s
              payer should have been switched, or a data or configuration
              problem upstream). No verdict is recorded and no accuracy figure
              changes. It comes back, tagged, if the engine later reads a value
              outside what it reads today.
            </span>
            <div className="flex flex-column gap-1">
              <label htmlFor="benefitWorklist.dismiss.note">
                Why are you hiding it? (optional)
              </label>
              <InputTextarea
                id="benefitWorklist.dismiss.note"
                value={note}
                rows={3}
                autoResize
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {isError && (
              <Message severity="error" text="It could not be saved." />
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
